/**
 * Agency-level Google Ads OAuth card for the Super Admin → Integrations tab.
 *
 * One control for the whole agency: connecting here authorizes the MCC and
 * covers every client account linked under the manager account. Per-workspace
 * settings only show read-only status (see GoogleAdsOAuthCard with readOnly).
 *
 * shadcn/ui to match the rest of AdminPage. Polls status every 30s so the
 * badge reflects health-check failures without manual refresh.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, RefreshCw, XCircle, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import {
  AGENCY_TENANT_SENTINEL,
  getLinkUrl,
  getConnectionStatus,
  unlink,
  type GoogleAdsConnectionStatus,
} from '@/services/googleAdsOAuthService';

export function AgencyGoogleAdsIntegrationCard() {
  const { currentWorkspace } = useWorkspaceStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ok = searchParams.get('google_ads_connected');
    const err = searchParams.get('google_ads_error');
    if (ok) {
      toast.success('Google Ads MCC connected — covers all clients under the manager account.');
      queryClient.invalidateQueries({
        queryKey: ['google-ads-oauth-status', AGENCY_TENANT_SENTINEL],
      });
    } else if (err) {
      toast.error(`Google Ads connection failed: ${decodeURIComponent(err)}`);
    }
    if (ok || err) {
      const next = new URLSearchParams(searchParams.toString());
      next.delete('google_ads_connected');
      next.delete('google_ads_error');
      next.delete('level');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient]);

  const { data: status, isLoading: loading } = useQuery<GoogleAdsConnectionStatus>({
    queryKey: ['google-ads-oauth-status', AGENCY_TENANT_SENTINEL],
    queryFn: () => getConnectionStatus(AGENCY_TENANT_SENTINEL),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  const handleConnect = async () => {
    if (!currentWorkspace?.id) {
      toast.error('No active workspace — cannot bind state token.');
      return;
    }
    setBusy(true);
    try {
      const url = await getLinkUrl({
        tenantId: AGENCY_TENANT_SENTINEL,
        workspaceId: String(currentWorkspace.id),
        userRole: 'admin',
      });
      window.location.href = url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't start Google authorization");
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        'Disconnect the agency MCC? All client Google Ads writes/reads will fail until reconnected.'
      )
    )
      return;
    setBusy(true);
    try {
      await unlink({ tenantId: AGENCY_TENANT_SENTINEL, userRole: 'admin' });
      await queryClient.invalidateQueries({
        queryKey: ['google-ads-oauth-status', AGENCY_TENANT_SENTINEL],
      });
      toast.info('Agency Google Ads link removed.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Disconnect failed');
    } finally {
      setBusy(false);
    }
  };

  const isConnected = !!status?.connected;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              {isConnected ? (
                <CheckCircle2 className="h-5 w-5 text-teal-dark" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-coral" />
              )}
              Google Ads (Agency MCC)
            </CardTitle>
            <CardDescription className="mt-1">
              One OAuth grant covers every client account linked under the manager account.
              Per-workspace settings show read-only status.
            </CardDescription>
          </div>
          <Badge
            variant="outline"
            className={
              isConnected
                ? 'bg-teal-light text-teal-dark border-teal/20'
                : 'bg-muted text-muted-foreground'
            }
          >
            {isConnected ? 'Connected' : 'Not connected'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Checking connection…</p>
        ) : isConnected ? (
          <>
            <div className="rounded-md border border-teal/20 bg-teal-light/40 p-4 text-sm space-y-1">
              <div className="font-medium text-teal-dark">
                {status?.level === 'agency'
                  ? 'Connected at agency (MCC) level'
                  : 'Connected (tenant-level)'}
              </div>
              {status?.external_account_id && (
                <div className="text-muted-foreground">
                  Account: <code className="text-foreground">{status.external_account_id}</code>
                </div>
              )}
              {status?.last_health_check_at && (
                <div className="text-muted-foreground">
                  Last health check: {new Date(status.last_health_check_at).toLocaleString()}
                </div>
              )}
              {status?.last_error && (
                <div className="text-destructive">Last error: {status.last_error}</div>
              )}
            </div>
            {/* Blast-radius warning. This is ONE credential covering every
                client under the manager account, so reconnecting with the
                wrong Google account silently repoints all of them — and
                nobody finds out until an operator opens a dashboard. That is
                not hypothetical: a stale agency token broke every MCC-managed
                tenant for ten days in July 2026, surfacing only as a browser
                error message.

                The role gate is blunt; saying what the button does is the
                part that actually prevents the mistake. */}
            <div className="rounded-md border border-amber/40 bg-amber-light/40 p-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">
                Agency-wide credential.
              </span>{' '}
              Reconnecting re-binds Google Ads for every client under this
              manager account, not just one workspace. To connect a single
              client&apos;s own account, use that workspace&apos;s Settings
              instead.
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleConnect}
                disabled={busy}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Reconnect
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDisconnect}
                disabled={busy}
                className="gap-2"
              >
                <XCircle className="h-4 w-4" />
                Disconnect
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Sign in with the Google account that has Admin access to the agency MCC. You'll
              authorize this app to read + write Google Ads for all linked client accounts.
            </p>
            <Button onClick={handleConnect} disabled={busy} className="gap-2">
              <LinkIcon className="h-4 w-4" />
              {busy ? 'Redirecting…' : 'Connect Google Ads'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
